import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  AnimatePresence,
} from "framer-motion";
import { SEO } from "../components/SEO";
import {
  Search,
  MessageSquare,
  FileCheck,
  ArrowRight,
  Shield,
  Building2,
  ChevronRight,
  BookOpen,
  Scale,
  FileText,
  Star,
  Menu,
  X,
  MapPin,
  Calculator,
  TrendingUp,
  HardHat,
  Ruler,
  DollarSign,
  AlertTriangle,
  Phone,
  Mail,
  ExternalLink,
} from "lucide-react";

// Animated counter component
function AnimatedCounter({
  end,
  duration = 2,
  suffix = "",
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;

    let startTime: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
}

// Blueprint grid background component
function BlueprintGrid() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Main grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(to right, #0d9488 1px, transparent 1px),
            linear-gradient(to bottom, #0d9488 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />
      {/* Fine grid */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `
            linear-gradient(to right, #0d9488 1px, transparent 1px),
            linear-gradient(to bottom, #0d9488 1px, transparent 1px)
          `,
          backgroundSize: "8px 8px",
        }}
      />
      {/* Decorative circles */}
      <svg
        className="absolute top-20 right-20 w-64 h-64 text-teal-200/30"
        viewBox="0 0 100 100"
      >
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="4 2"
        />
        <circle
          cx="50"
          cy="50"
          r="35"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.5"
        />
        <circle
          cx="50"
          cy="50"
          r="25"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="2 4"
        />
      </svg>
      {/* Corner marks */}
      <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-teal-200/20" />
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-teal-200/20" />
    </div>
  );
}

// Navigation
function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-900/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center overflow-hidden shadow-lg shadow-teal-500/20">
                <HardHat className="w-5 h-5 text-white" />
                <div className="absolute inset-0 bg-gradient-to-t from-white/10 to-transparent" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-slate-900 tracking-tight">
                  Builder Buddy
                </span>
                <span className="text-[10px] font-medium text-teal-600 tracking-widest uppercase">
                  Canada
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {["Features", "Stats", "Tools"].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  {item}
                </a>
              ))}
              <Link
                to="/blog"
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Blog
              </Link>
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/chat"
                className="px-5 py-2.5 text-sm font-semibold text-white bg-teal-500 hover:bg-teal-600 rounded-xl transition-all shadow-lg shadow-teal-500/20 hover:shadow-teal-500/30 hover:-translate-y-0.5"
              >
                Get Started Free
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-[72px] z-40 bg-white border-b border-slate-200 shadow-xl md:hidden"
          >
            <div className="p-6 space-y-4">
              {["Features", "Stats", "Tools"].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-lg font-medium text-slate-700"
                >
                  {item}
                </a>
              ))}
              <Link
                to="/blog"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-lg font-medium text-slate-700"
              >
                Blog
              </Link>
              <div className="pt-4 border-t border-slate-100">
                <Link
                  to="/chat"
                  className="block w-full text-center py-3 bg-teal-500 text-white font-semibold rounded-xl"
                >
                  Get Started Free
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Hero Section
function HeroSection() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
      <BlueprintGrid />

      {/* Gradient orbs */}
      <motion.div
        style={{ y }}
        className="absolute top-1/4 -left-32 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl"
      />
      <motion.div
        style={{ y: useTransform(scrollY, [0, 500], [0, -100]) }}
        className="absolute bottom-1/4 -right-32 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left column - Text content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full text-teal-700 text-sm font-medium mb-8"
            >
              <MapPin className="w-4 h-4 fill-teal-400 text-teal-400" />
              <span>Trusted by Calgary Builders, Contractors & Developers</span>
            </motion.div>

            {/* Main headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] tracking-tight mb-6"
            >
              Professional{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-500">
                  Cost Estimates
                </span>
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  viewBox="0 0 200 12"
                  fill="none"
                >
                  <path
                    d="M2 8 Q 100 2, 198 8"
                    stroke="#14b8a6"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              for Calgary
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-xl text-slate-600 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed"
            >
              Free professional-grade tools for builders, contractors, and
              developers. Alberta building codes, CIQS-compliant quantity
              surveys, and feasibility studies — built with 2026 Calgary market
              data.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10"
            >
              <Link
                to="/quantity-survey"
                className="group flex items-center gap-2 px-8 py-4 bg-teal-500 text-white font-semibold rounded-xl shadow-2xl shadow-teal-500/20 hover:shadow-teal-500/30 hover:bg-teal-600 hover:-translate-y-1 transition-all"
              >
                <Calculator className="w-5 h-5" />
                Free Cost Estimate
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/feasibility"
                className="group flex items-center gap-2 px-8 py-4 bg-white text-slate-700 font-semibold rounded-xl border-2 border-slate-200 hover:border-teal-300 hover:bg-teal-50 transition-all"
              >
                <TrendingUp className="w-5 h-5" />
                Feasibility Study
              </Link>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-slate-400 text-sm"
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-teal-500" />
                <span>100% Free</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-teal-500" />
                <span>Alberta Building Code</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-teal-500" />
                <span>2026 Calgary Rates</span>
              </div>
            </motion.div>
          </div>

          {/* Right column - Feature Preview */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:pl-8"
          >
            <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden">
              {/* Chat Preview Header */}
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Builder Buddy</h3>
                    <p className="text-xs text-teal-100">Always here to help</p>
                  </div>
                </div>
              </div>

              {/* Chat Messages Preview */}
              <div className="p-6 space-y-4 bg-slate-50">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <HardHat className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm max-w-[80%]">
                    <p className="text-sm text-slate-700">
                      What's the minimum stair width for a residential building?
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <div className="bg-teal-500 rounded-2xl rounded-tr-none px-4 py-3 shadow-sm max-w-[80%]">
                    <p className="text-sm text-white">
                      According to NBC 9.8.4.1, the minimum stair width for
                      residential buildings is{" "}
                      <span className="font-semibold">860 mm</span> (33.9
                      inches) between guards or walls.
                    </p>
                    <div className="mt-2 pt-2 border-t border-teal-400/30">
                      <p className="text-xs text-teal-100">
                        📖 NBC(AE) 2023 - Part 9
                      </p>
                    </div>
                  </div>
                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>

              {/* Quick Action Chips */}
              <div className="px-6 py-4 border-t border-slate-200 bg-white">
                <p className="text-xs text-slate-400 mb-2">Try asking about:</p>
                <div className="flex flex-wrap gap-2">
                  {["Setback requirements", "Fire ratings", "Permit fees"].map(
                    (q) => (
                      <span
                        key={q}
                        className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs rounded-full"
                      >
                        {q}
                      </span>
                    ),
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Features Section
function FeaturesSection() {
  const features = [
    {
      icon: Calculator,
      title: "Quantity Survey",
      subtitle: "CIQS Methodology",
      description:
        "Generate professional cost estimates using CIQS-compliant quantity takeoffs. Based on 2026 Calgary material and labor rates. Export detailed bills of quantities for client presentations.",
      gradient: "from-rose-500 to-pink-500",
      link: "/quantity-survey",
      highlight: true,
    },
    {
      icon: TrendingUp,
      title: "Feasibility Study",
      subtitle: "Calgary Market Data",
      description:
        "Analyze project feasibility with real Calgary land costs, government incentives, and material price comparisons. Make informed investment decisions with accurate local data.",
      gradient: "from-cyan-500 to-teal-500",
      link: "/feasibility",
      highlight: true,
    },
    {
      icon: MessageSquare,
      title: "AI Chat",
      subtitle: "Alberta Building Code",
      description:
        "Get instant answers to building code questions with citations from NBC(AE) 2023 and Calgary Land Use Bylaw. Perfect for quick code lookups on site.",
      gradient: "from-teal-500 to-emerald-500",
      link: "/chat",
    },
    {
      icon: Scale,
      title: "SDAB Expert",
      subtitle: "Risk Assessment",
      description:
        "AI analysis of 1,400+ Calgary SDAB decisions. Assess your development permit risk before submission and get recommendations to improve approval odds.",
      gradient: "from-amber-500 to-orange-500",
      link: "/sdab",
    },
    {
      icon: Search,
      title: "Code Library",
      subtitle: "Search & Browse",
      description:
        "Search across Alberta building codes, Calgary land use bylaws, and fire codes. Find the exact clause you need in seconds with full-text search.",
      gradient: "from-blue-500 to-indigo-500",
      link: "/explore",
    },
    {
      icon: FileCheck,
      title: "Permit Guide",
      subtitle: "Know What You Need",
      description:
        "Find out which permits and approvals you need for residential and commercial building projects in Calgary. Get requirements, checklists, and submission guidance.",
      gradient: "from-violet-500 to-purple-500",
      link: "/permits",
    },
  ];

  return (
    <section id="features" className="py-32 bg-white relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 text-sm font-medium rounded-full mb-4">
            All-in-One Platform
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Everything You Need to{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-500">
              Build Better
            </span>
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            From code research to cost estimation, Builder Buddy has you covered
            at every stage of your construction project.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={feature.link}
                className={`group block h-full p-8 rounded-2xl bg-white border shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 ${
                  feature.highlight
                    ? "border-teal-300 ring-2 ring-teal-100"
                    : "border-slate-200 hover:border-teal-300"
                }`}
              >
                {/* Popular badge for highlighted features */}
                {feature.highlight && (
                  <div className="inline-flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full mb-4">
                    <Star className="w-3 h-3 fill-teal-500" />
                    Most Popular
                  </div>
                )}

                {/* Icon */}
                <div
                  className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${feature.gradient} mb-6 shadow-lg`}
                >
                  <feature.icon className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-2xl font-bold text-slate-900">
                      {feature.title}
                    </h3>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-teal-500 group-hover:translate-x-1 transition-all" />
                  </div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                    {feature.subtitle}
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                {/* Bottom decoration */}
                <div className="mt-8 pt-6 border-t border-slate-100">
                  <span className="text-sm font-medium text-teal-600 group-hover:text-teal-500">
                    {feature.highlight
                      ? "Start Free Estimate"
                      : `Try ${feature.title}`}{" "}
                    →
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Social Proof Section - Calgary Focus
function SocialProofSection() {
  const proofPoints = [
    { value: "1,400+", label: "Calgary Industry Professionals" },
    { value: "2026", label: "Calgary Market Rates" },
    { value: "NBC(AE)", label: "Alberta Building Code" },
    { value: "CIQS", label: "Compliant Methodology" },
  ];

  return (
    <section className="py-16 bg-gradient-to-r from-teal-600 to-emerald-600 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
          className="w-full h-full"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Built for Calgary's Construction Industry
          </h2>
          <p className="text-teal-100">
            The go-to platform for CCA and BILD Calgary Region members —
            builders, GCs, developers, and consultants
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {proofPoints.map((point, index) => (
            <motion.div
              key={point.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20"
            >
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {point.value}
              </div>
              <p className="text-sm text-teal-100">{point.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Professional Benefits Section
function ProfessionalBenefitsSection() {
  const benefits = [
    {
      role: "For Builders & Contractors",
      icon: HardHat,
      description: "Speed up repeat permits and estimate costs across projects",
      details:
        "Quickly check code requirements, generate cost estimates for multiple projects, and track permit needs. Built for builders who manage several jobs at once.",
      color: "from-teal-500 to-emerald-500",
    },
    {
      role: "For Developers & Investors",
      icon: TrendingUp,
      description:
        "Feasibility analysis with Calgary land costs and ROI projections",
      details:
        "Analyze development feasibility with real Calgary land values, construction costs, government incentives, and market comparables. Make confident investment decisions.",
      color: "from-emerald-500 to-teal-500",
    },
    {
      role: "For General Contractors",
      icon: Building2,
      description: "Code compliance and cost control for every trade",
      details:
        "Look up building code requirements on site, verify subcontractor work meets NBC standards, and generate accurate project budgets with Calgary labor rates.",
      color: "from-blue-500 to-indigo-500",
    },
    {
      role: "For Architects & Consultants",
      icon: Ruler,
      description: "Professional BOQ reports and code verification for clients",
      details:
        "Generate CIQS-compliant quantity takeoffs for client presentations, verify designs against NBC 2023, and streamline permit applications with accurate documentation.",
      color: "from-orange-500 to-amber-500",
    },
    {
      role: "For Permit Expediters",
      icon: FileCheck,
      description: "Instant code lookups and permit requirement checks",
      details:
        "Quickly determine which permits are needed, check zoning compliance, and verify code requirements. Save hours of research on every application you process.",
      color: "from-violet-500 to-purple-500",
    },
    {
      role: "For Homeowners & Renovators",
      icon: Calculator,
      description: "Know your renovation costs before hiring a contractor",
      details:
        "Get realistic cost estimates for your home project. Compare contractor quotes with confidence using our free tools and 2026 Calgary pricing data.",
      color: "from-rose-500 to-pink-500",
    },
  ];

  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 text-sm font-medium rounded-full mb-4">
            Built for Professionals
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Tools for Every Professional in Calgary Construction
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Whether you're a builder managing multiple projects, a developer
            analyzing ROI, or a consultant preparing client deliverables —
            Builder Buddy is built for you.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.role}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all"
            >
              <div className="flex items-start gap-5">
                <div
                  className={`flex-shrink-0 p-4 rounded-xl bg-gradient-to-br ${benefit.color}`}
                >
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">
                    {benefit.role}
                  </h3>
                  <p className="text-teal-600 font-medium mb-3">
                    {benefit.description}
                  </p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {benefit.details}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/quantity-survey"
              className="group flex items-center gap-2 px-6 py-3 bg-teal-500 text-white font-semibold rounded-xl shadow-lg hover:bg-teal-600 hover:-translate-y-0.5 transition-all"
            >
              <Calculator className="w-5 h-5" />
              Try Free Cost Estimate
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/chat"
              className="flex items-center gap-2 px-6 py-3 text-slate-600 font-medium hover:text-teal-600 transition-colors"
            >
              <MessageSquare className="w-5 h-5" />
              Or Ask a Code Question
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Statistics Section
function StatisticsSection() {
  const stats = [
    { value: 505, label: "NBC Articles", suffix: "+", icon: BookOpen },
    { value: 188019, label: "Development Permits", suffix: "", icon: FileText },
    { value: 1433, label: "SDAB Decisions", suffix: "", icon: Scale },
    { value: 414605, label: "Canadian Addresses", suffix: "", icon: MapPin },
  ];

  return (
    <section id="stats" className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Blueprint grid dark */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(to right, #14b8a6 1px, transparent 1px),
            linear-gradient(to bottom, #14b8a6 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-transparent to-emerald-500/5" />

      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Powered by Real Canadian Data
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Our AI is trained on years of permit history, SDAB decisions, and
            regulatory updates. Currently featuring Alberta data, with more
            provinces coming soon.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <div className="inline-flex p-4 rounded-2xl bg-white/5 mb-4">
                <stat.icon className="w-8 h-8 text-teal-400" />
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                <AnimatedCounter end={stat.value} suffix={stat.suffix} />
              </div>
              <p className="text-slate-400">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Tools Section (What You Can Do)
function ToolsSection() {
  const tools = [
    {
      icon: MessageSquare,
      title: "Ask building code questions",
      description: "Get instant, cited answers from NBC(AE) 2023",
    },
    {
      icon: Calculator,
      title: "Estimate project costs",
      description: "Generate detailed quantity surveys",
    },
    {
      icon: AlertTriangle,
      title: "Assess development risk",
      description: "AI analysis of similar SDAB cases",
    },
    {
      icon: DollarSign,
      title: "Find government incentives",
      description: "Discover rebates and grants for your project",
    },
    {
      icon: Ruler,
      title: "Check zoning requirements",
      description: "Verify setbacks, heights, and coverage",
    },
  ];

  return (
    <section
      id="tools"
      className="py-24 bg-gradient-to-b from-white to-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            What Can Builder Buddy Do?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Everything you need to navigate Calgary's building codes and permit
            process
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((tool, index) => (
            <motion.div
              key={tool.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="flex items-start gap-4 p-6 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all"
            >
              <div className="p-3 bg-teal-50 rounded-xl">
                <tool.icon className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  {tool.title}
                </h3>
                <p className="text-sm text-slate-600">{tool.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection() {
  return (
    <section className="py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Build{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
              Smarter
            </span>
            ?
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Join builders, contractors, developers, and consultants across
            Calgary who save hours on code research, cost estimation, and permit
            applications. 100% free — no signup required.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link
              to="/chat"
              className="group flex items-center gap-2 px-8 py-4 bg-teal-500 text-white font-semibold rounded-xl shadow-2xl shadow-teal-500/20 hover:shadow-teal-500/30 hover:bg-teal-400 hover:-translate-y-1 transition-all"
            >
              <MessageSquare className="w-5 h-5" />
              Start Chatting Now
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/explore"
              className="flex items-center gap-2 px-8 py-4 text-white font-medium hover:text-teal-400 transition-colors"
            >
              <Search className="w-5 h-5" />
              Or Browse Code Library
            </Link>
          </div>

          {/* Contact Info */}
          <div className="max-w-md mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <p className="text-sm text-slate-300 mb-3">
              For support and questions:
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:+14033335025"
                className="flex items-center justify-center gap-2 text-white hover:text-teal-300 transition-colors"
              >
                <Phone className="w-4 h-4" />
                (403) 333-5025
              </a>
              <a
                href="mailto:Tam.Crete.engineering@gmail.com"
                className="flex items-center justify-center gap-2 text-white hover:text-teal-300 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Contact Us
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  const footerLinks = {
    "Cost Tools": [
      { label: "Quantity Survey", href: "/quantity-survey" },
      { label: "Feasibility Study", href: "/feasibility" },
      { label: "SDAB Risk Assessment", href: "/sdab" },
    ],
    "Code Resources": [
      { label: "AI Chat", href: "/chat" },
      { label: "Code Library", href: "/explore" },
      { label: "Permit Guide", href: "/permits" },
      { label: "Permit Info", href: "/permits/info" },
      { label: "Blog", href: "/blog" },
    ],
    Legal: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
    ],
  };

  return (
    <footer className="bg-slate-900 border-t border-slate-800">
      {/* Calgary Trust Signals Banner */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-12 text-sm">
            <div className="flex items-center gap-2 text-slate-300">
              <MapPin className="w-4 h-4 text-teal-400" />
              <span>Serving Calgary & Alberta</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <BookOpen className="w-4 h-4 text-teal-400" />
              <span>NBC(AE) 2023 Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <FileText className="w-4 h-4 text-teal-400" />
              <span>Calgary Land Use Bylaw</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <Calculator className="w-4 h-4 text-teal-400" />
              <span>CIQS Methodology</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <HardHat className="w-4 h-4 text-teal-400" />
              <span>For CCA & BILD Members</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-white">Builder Buddy</span>
                <span className="block text-[10px] text-teal-400 tracking-widest uppercase">
                  Calgary
                </span>
              </div>
            </Link>
            <p className="text-sm text-slate-400 mb-4">
              Free professional-grade tools for Calgary builders, contractors,
              developers, architects, and consultants.
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
              <MapPin className="w-4 h-4" />
              <span>Calgary, Alberta, Canada</span>
            </div>
            {/* Contact Info */}
            <div className="mt-4 space-y-2">
              <p className="text-sm text-slate-400 font-medium">Support:</p>
              <a
                href="tel:+14033335025"
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors"
              >
                <Phone className="w-4 h-4" />
                (403) 333-5025
              </a>
              <a
                href="mailto:Tam.Crete.engineering@gmail.com"
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Contact Us
              </a>
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-semibold text-white mb-4">{category}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-slate-400 hover:text-teal-400 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Builder Buddy Calgary. All rights
              reserved. Data based on 2026 Calgary market rates.
            </p>
            <p className="text-sm text-slate-400">
              A subsidiary of{" "}
              <a
                href="https://tamcrete-eng.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-400 hover:text-teal-300 transition-colors inline-flex items-center gap-1"
              >
                Tam-Crete Engineering
                <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Main Landing Page Component
export function LandingPage() {
  return (
    <>
      <SEO />
      <div className="min-h-screen">
        <Navigation />
        <main>
          <HeroSection />
          <SocialProofSection />
          <FeaturesSection />
          <ProfessionalBenefitsSection />
          <StatisticsSection />
          <ToolsSection />
          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
}

export default LandingPage;
